import { observer } from "mobx-react-lite";

import { useSnippetOverrideStore } from "@/components/Playground/playground-store";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const getVariantDisplayText = (variant: { label?: string; version?: number }): string => {
  if (variant.label) {
    return variant.label;
  }

  if (variant.version) {
    return `v${variant.version}`;
  }

  return "Latest";
};

interface ConfirmSnippetChangesProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmSnippetChanges = observer(
  ({ isOpen, onConfirm, onCancel }: ConfirmSnippetChangesProps) => {
    const snippetOverrides = useSnippetOverrideStore();

    const handleConfirm = () => {
      onConfirm();
    };

    const handleCancel = () => {
      onCancel();
    };

    const setIsOpen = (open: boolean) => {
      if (!open) {
        onCancel();
      }
    };

    const overrides = snippetOverrides?.snippetOverrides || {};
    const hasOverrides = Object.keys(overrides).length > 0;

    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Confirm Snippet Updates</DialogTitle>
            <DialogDescription>
              Saving will create new versions and update references for the snippets below.
            </DialogDescription>
          </DialogHeader>
          {hasOverrides && (
            <div className="my-4 max-h-96 overflow-y-auto rounded-md border border-gray-200 bg-gray-50 p-4">
              <h4 className="mb-3 text-sm font-semibold text-gray-700">
                New Snippet Versions (will replace existing references in prompt):
              </h4>
              <div className="space-y-3">
                {Object.entries(snippetOverrides?.overridesWithMetadata || {}).map(
                  ([snippetKey, { value: content, variants }]) => {
                    // Extract prompt name from normalized key @@@promptName@@@
                    const match = snippetKey.match(/@@@([^@]+)@@@/);
                    const name = match?.[1] || snippetKey;

                    return (
                      <div key={snippetKey} className="rounded bg-blue-50/30">
                        <div className="flex flex-col gap-1 rounded-t-md bg-amber-500 p-2">
                          <span className="min-w-0 truncate font-mono text-xs font-medium text-white">
                            Snippet: {name}
                          </span>
                          <div className="flex flex-wrap gap-1">
                            {(() => {
                              const displayTexts = variants.map(variant =>
                                getVariantDisplayText(variant),
                              );
                              const aggregatedText = displayTexts.join(", ");
                              return (
                                <span className="flex-shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                                  {aggregatedText} → Latest
                                </span>
                              );
                            })()}
                          </div>
                        </div>
                        <div className="max-h-32 overflow-y-auto rounded-b-md border border-t-0 border-dashed border-amber-400 px-3 py-2">
                          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                            New snippet content:
                          </div>
                          <pre className="whitespace-pre-wrap break-words text-xs text-gray-600">
                            {content}
                          </pre>
                        </div>
                      </div>
                    );
                  },
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleConfirm}>Continue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  },
);

export default ConfirmSnippetChanges;
